/* eslint-disable import/max-dependencies */
import type { FC as FunctionComponent } from "react";
import React from "react";
import { Helmet } from "react-helmet";
import { withStyles } from "@mui/styles";
import {
  SetupCard,
  withState,
  TrackingCodeV2,
  AdditionalPostbacks as AdditionalPostbacksList,
  withHookPropsState,
  EventPostbackList,
} from "@fas/cpa-cabinet-ui";
import { useEventsPostback } from "@fas/cpa-state-manager/redux/hooks/useEventsPostback";
import {
  useTrackingTools,
  useAdditionalPostbacks,
} from "@fas/cpa-state-manager/redux/hooks";
import { Navigate } from "react-router-dom";
import environment from "environment";
import TabsSwitcher from "@fas/cpa-cabinet-ui/lib/TabsSwitcher";
import { Box } from "@mui/material";
import {
  setupCardStyles,
  trackingCodeStyles,
  additionalPostbackStyles,
  eventPostbackStyles,
} from "../../styles";
import { useAddInfoPostbacks, useTabs } from "../../hooks";
import { POSTBACKS_PAGE } from "../../constants";
import PostbackLayout from "./PostbackLayout";
import withNoData from "../WithNoData";
import NoData from "../NoPostbacksData";
import ToolTip from "../Reports/Tooltip";
import tabsSwitcher from "../../styles/tabsSwitcher";
import SelectPopup from "../SelectPopup";
import { hiddenSearchInputClearIconSelectPopup } from "../../styles/hiddenSearchInputSelectPopup";
import AdditionalTabButton from "../Reports/AdditionalTabButton";


const EventPostbacks = withNoData(EventPostbackList, () => (
  <NoData
    title="No info postback yet"
    description="You currently have no info postbacks. Set them up to see other events (such as confirmations or payments) besides those set in the main postback. To do this, please go to the informational events postback section, press the Create new postback button in the upper right corner, and follow the instructions."
  />
));
const StyledSetupCard = withStyles(setupCardStyles)(SetupCard);
const StyledInfoPostbacks = withStyles(eventPostbackStyles)(
  EventPostbacks
) as typeof EventPostbacks;
const InfoPostbacks = withState(StyledInfoPostbacks, useEventsPostback);
const StyledTrackingCode = withStyles(trackingCodeStyles)(
  TrackingCodeV2
) as typeof TrackingCodeV2;
const TrackingCode = withState(StyledTrackingCode, useTrackingTools);
const AdditionalPostbacks = withState(
  AdditionalPostbacksList,
  useAdditionalPostbacks
);
const StyledAdditionalPostbacks = withStyles(additionalPostbackStyles)(
  AdditionalPostbacks
);
const StyledTabs = withStyles(tabsSwitcher)(TabsSwitcher);
const Tabs = withHookPropsState(StyledTabs, useTabs);
const AddPostbackButton = withState(
  AdditionalTabButton,
  useAddInfoPostbacks
);
export const paramsToComponents = {
  defaulPostback: () => (
    <PostbackLayout>
      <Helmet>
        <title>
          Default postback
        </title>
      </Helmet>
      <Box
        sx={{
          marginBottom: {
            xs: "40px",
          },
        }}
      >
        <TrackingCode
          label="Tracking code"
          helperText='See "how to" block below for the details'
          textFieldVariant="standard"
          buttonSize="small"
        />
      </Box>
    </PostbackLayout>
  ),
  additionalPostback: () => (
    <PostbackLayout>
      <Helmet>
        <title>
          Additional postbacks
        </title>
      </Helmet>
      <Box mb={4}>
        <StyledAdditionalPostbacks
          NoData={() => (
            <NoData
              title="No additional postback yet"
              description="You currently have no additional postbacks. If you need to add postbacks from different tracking platforms, please go to the additional postbacks section. To set up an additional postback, feel free to contact your personal manager."
            />
          )}
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          Tooltip={ToolTip}
        />
      </Box>
    </PostbackLayout>
  ),
  infoPostback: () => (
    <PostbackLayout>
      <Helmet>
        <title>
          Info event postbacks
        </title>
      </Helmet>
      <Box
        sx={{
          marginBottom: {
            xs: "40px",
          },
        }}
      >
        <InfoPostbacks
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          sx={{
            marginBottom: {
              xs: "40px",
              sm: "0",
            },
          }}
          components={{
            SelectField: withStyles(hiddenSearchInputClearIconSelectPopup)(
              withState(SelectPopup, () => ({
                formControlSx: {},
                disableSearch: true,
              }))
            ) as any,
            MultiSelectField: withStyles({
              inputField: {
                "& ::placeholder": {
                  opacity: 1,
                },
              },
            })(
              withState(SelectPopup, () => ({
                formControlSx: {},
                isSplitListBoxColumn: true,
                placeholder: "All",
              }))
            ) as any,
          }}
        />
      </Box>
    </PostbackLayout>
  ),
  default: () => <Navigate to={environment.links.notFound} />,
};

type Props = {
  param: keyof typeof paramsToComponents;
};

const Postbacks: FunctionComponent<Props> = ({ param }) => (
  <StyledSetupCard title="Tracking Tools">
    <Box display="flex" alignItems="flex-end">
      <Box flexGrow={1} minWidth={0}>
        <Tabs
          hookProps={{
            key: POSTBACKS_PAGE,
          }}
        />
      </Box>
      <AddPostbackButton />
    </Box>
    {paramsToComponents[param]()}
  </StyledSetupCard>
);

export default Postbacks;
